import React from 'react';
import { AlertTriangle, RefreshCw } from 'react-feather';

/**
 * Reusable error + retry surface (BUG_LIST #165 root cause, #172, #173, #182,
 * #195, #233, etc.). Replaces the dominant "silent catch → confident empty
 * state" anti-pattern: when a fetch fails, render THIS instead of "No results",
 * so the user knows something broke and can retry.
 */
interface ErrorStateProps {
  message?: string;
  onRetry?: () => void;
  /** Compact inline variant (small, left-aligned) vs. the default centered panel. */
  compact?: boolean;
  retryLabel?: string;
  style?: React.CSSProperties;
}

const ErrorState: React.FC<ErrorStateProps> = ({
  message = 'Something went wrong. Please try again.',
  onRetry,
  compact = false,
  retryLabel = 'Retry',
  style,
}) => {
  if (compact) {
    return (
      <div
        role="alert"
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '12px 16px',
          background: '#FEF2F2',
          border: '1px solid #FECACA',
          borderRadius: '8px',
          color: '#991B1B',
          fontSize: '14px',
          fontFamily: 'Manrope, sans-serif',
          ...style,
        }}
      >
        <AlertTriangle size={16} color="#DC2626" style={{ flexShrink: 0 }} />
        <span style={{ flex: 1 }}>{message}</span>
        {onRetry && (
          <button
            onClick={onRetry}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              padding: '6px 12px',
              background: 'white',
              border: '1px solid #FCA5A5',
              borderRadius: '6px',
              color: '#DC2626',
              fontSize: '13px',
              fontWeight: 600,
              cursor: 'pointer',
              fontFamily: 'Manrope, sans-serif',
            }}
          >
            <RefreshCw size={13} aria-hidden="true" />
            {retryLabel}
          </button>
        )}
      </div>
    );
  }

  return (
    <div
      role="alert"
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '12px',
        padding: '48px 24px',
        textAlign: 'center',
        color: '#6B7280',
        fontFamily: 'Manrope, sans-serif',
        ...style,
      }}
    >
      <div
        style={{
          width: '56px',
          height: '56px',
          borderRadius: '50%',
          background: '#FEE2E2',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <AlertTriangle size={26} color="#DC2626" aria-hidden="true" />
      </div>
      <div style={{ fontSize: '16px', fontWeight: 600, color: '#374151', maxWidth: 420 }}>{message}</div>
      {onRetry && (
        <button
          onClick={onRetry}
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '8px',
            marginTop: '4px',
            padding: '10px 20px',
            background: '#F9550D',
            border: 'none',
            borderRadius: '8px',
            color: 'white',
            fontSize: '14px',
            fontWeight: 600,
            cursor: 'pointer',
            fontFamily: 'Manrope, sans-serif',
          }}
        >
          <RefreshCw size={15} aria-hidden="true" />
          {retryLabel}
        </button>
      )}
    </div>
  );
};

export default ErrorState;
